import { Controller } from "@hotwired/stimulus"
import { showOpsToast, csrfToken, formatErrors } from "../lib/ops_toast"

// Clickable button that submits a Plutonium interaction via JSON and
// surfaces validation errors as an inline toast. On success, the
// Order/KitchenTicket turbo broadcast replaces the affected card —
// nothing to do client-side. For actions that need a reason (void,
// cancel), set `prompt-value` and `param-key-value`; the controller
// collects the reason via window.prompt() and submits it under
// `<paramKey>[reason]`.
export default class extends Controller {
  static values = {
    url: String,
    confirm: String,
    prompt: String,
    paramKey: String
  }

  async click(event) {
    event.preventDefault()

    let reason = null
    if (this.hasPromptValue && this.promptValue) {
      reason = window.prompt(this.promptValue)
      if (reason === null) return // user cancelled
      reason = reason.trim()
      if (reason === "") {
        showOpsToast("Reason is required", "error")
        return
      }
    } else if (this.hasConfirmValue && this.confirmValue) {
      if (!window.confirm(this.confirmValue)) return
    }

    this.element.disabled = true

    try {
      const formData = new FormData()
      if (reason !== null) {
        const key = this.hasParamKeyValue ? this.paramKeyValue : "interaction"
        formData.append(`${key}[reason]`, reason)
      }

      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": csrfToken()
        },
        body: formData
      })

      if (response.ok) {
        // Success — the model's Turbo broadcast will replace the card.
        const undoUrl = this.element.dataset.opsUndoUrl
        if (undoUrl) {
          document.dispatchEvent(new CustomEvent("ops-toasts:show", {
            detail: {
              label: this.element.dataset.opsUndoLabel,
              actionLabel: "Undo",
              url: undoUrl
            }
          }))
        }
        return
      }

      if (response.status === 422 || response.status === 403) {
        const payload = await response.json().catch(() => ({}))
        showOpsToast(formatErrors(payload), "error")
      } else {
        showOpsToast(`Request failed (${response.status})`, "error")
      }
    } catch (err) {
      showOpsToast(`Network error: ${err.message}`, "error")
    } finally {
      this.element.disabled = false
    }
  }
}
